<template>
  <div
    class="text-static-default-hi rounded-lg flex w-full items-center px-16 py-20"
    :class="bannerType.backgroundClass"
  >
    <component :is="bannerType.icon" class="mr-10" />

    <div class="space-x-1">
      <component
        :is="element.tag"
        v-for="element in bannerElements"
        :key="element.content"
        v-bind="element.attributes"
        @click="handleLinkClick(element)"
      >
        {{ element.content }}
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import type { Component } from 'nuxt/schema'

import type { Banner, BannerLink } from '~/api/banners/types'

import { BANNER_TYPES, LINK_IDENTIFIER } from './TheAutonomousBanner.constants'

const tracking = useTracking()
const currentRoute = useRoute()

const props = defineProps<{
  banner: Banner
}>()

type BannerElement = {
  tag: string | Component
  attributes?: {
    variant: string
    to: string
    target: string
  }
  tagName?: string
  content: string
}

const bannerElements = computed(() => {
  const bannerParts = props.banner.content.split(LINK_IDENTIFIER)

  return bannerParts.map((part): BannerElement => {
    const associatedLink = props.banner.links.find((link) => link.id === part)

    if (associatedLink) {
      return {
        tag: RevLink as unknown as Component,
        attributes: {
          variant: 'default',
          to: (associatedLink.link as BannerLink).href,
          target: '_blank',
        },
        content: associatedLink.content as string,
        tagName: 'RevLink',
      }
    }

    return {
      tag: 'span',
      tagName: 'span',
      content: part,
    }
  })
})

const bannerType = computed(() => {
  return BANNER_TYPES[props.banner.type] || BANNER_TYPES.INFO
})

function handleLinkClick(element: BannerElement) {
  if (element.tagName !== 'RevLink') return

  tracking.trackClick({
    name: 'communication-banner-link',
    bannerName: props.banner.name,
    bannerContent: props.banner.content,
    pageName: currentRoute.meta.autonomousBannerPageName as string,
  })
}
</script>
